import React from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import SEO from "@/components/site/SEO";
import Reveal from "@/components/site/Reveal";

const Section = ({ heading, children }) => (
    <div className="mt-10">
        <h2 className="font-serif-editorial text-2xl sm:text-3xl text-brand-primary mb-3 leading-tight">
            {heading}
        </h2>
        <div className="text-brand-text/85 leading-relaxed space-y-4">
            {children}
        </div>
    </div>
);

const ContactCard = ({ name, address, telephone, email, regNo }) => (
    <div className="bg-brand-surface border border-brand-border rounded-xl p-5 space-y-3">
        <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold">
            {name}
        </div>
        <div className="flex items-start gap-2 text-sm">
            <MapPin size={16} className="text-brand-primary mt-0.5 shrink-0" />
            <span className="text-brand-text/85 leading-relaxed">{address}</span>
        </div>
        <div className="flex items-center gap-2 text-sm">
            <Phone size={16} className="text-brand-primary shrink-0" />
            <a
                href={`tel:${telephone.replace(/\s/g, "")}`}
                className="link-underline text-brand-primary font-semibold"
            >
                {telephone}
            </a>
        </div>
        <div className="flex items-center gap-2 text-sm">
            <Mail size={16} className="text-brand-primary shrink-0" />
            <a
                href={`mailto:${email}`}
                className="link-underline text-brand-primary font-semibold break-all"
            >
                {email}
            </a>
        </div>
        <div className="text-xs text-brand-muted pt-2 border-t border-brand-border">
            Data Controller Registration:{" "}
            <strong className="text-brand-primary">{regNo}</strong>
        </div>
    </div>
);

export default function Privacy() {
    return (
        <>
            <SEO
                title="Privacy Policy | Acumen Financial Services"
                description="How Acumen Financial Services Ltd and HL Partnership Limited handle your personal data, meet UK Data Protection Act requirements, and how to contact our Data Controller or the ICO."
                path="/privacy"
            />

            <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 bg-brand-bg">
                <div className="mx-auto max-w-3xl px-6 md:px-10">
                    <Reveal>
                        <div className="flex items-center gap-3 mb-6">
                            <span className="h-px w-10 bg-brand-accent" />
                            <span className="text-xs uppercase tracking-[0.24em] text-brand-accent font-bold">
                                Privacy policy
                            </span>
                        </div>
                        <h1
                            data-testid="privacy-title"
                            className="font-serif-editorial text-5xl sm:text-6xl tracking-tight text-brand-primary leading-[1.05]"
                        >
                            How we handle your personal data.
                        </h1>
                        <p className="mt-6 text-lg text-brand-muted leading-relaxed">
                            A guide to how we meet the requirements of the UK Data
                            Protection Act. Making sure personal data is secure and
                            properly dealt with is of paramount importance in our
                            dealings with you. It is therefore important you understand
                            what information we will take, why we need it, and how we
                            will process your details.
                        </p>
                    </Reveal>

                    <Reveal>
                        <Section heading="Who will process my data?">
                            <p>
                                Acumen Financial Services Ltd, is an appointed
                                representative of HL Partnership Limited which is
                                authorised and regulated by the Financial Conduct
                                Authority. As such, both Acumen Financial Services Ltd
                                and HL Partnership Limited will process your data in
                                order to provide our services.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="What data will you ask me for?">
                            <p>
                                In order to fully understand your financial needs and be
                                able to give good advice, we need to obtain information
                                about you, your family, your personal circumstances, and
                                your preferences. Some of the information will be
                                private and may be sensitive, such as details of your
                                income and expenditure, health, debts and credit
                                commitments, legal proceedings and adverse credit. Much
                                of the information you provide will need to be passed on
                                to the companies whose products you apply for.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="Do I have to provide my personal data?">
                            <p>
                                In order for us to be able to give you suitable advice,
                                we need you to provide all the information we ask for as
                                openly and honestly as possible. If you prefer not to
                                disclose some information, please tell us that you
                                prefer not to disclose it and we will assess whether
                                this will alter or impede the work we are doing for you.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="How will you use my data?">
                            <p>
                                <strong className="text-brand-primary">1.</strong> The
                                primary reason we will use your data is to allow us to
                                do the job you've asked us to do. Specifically, we will
                                use your data to:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Assess and provide advice to meet your needs and circumstances;</li>
                                <li>Introduce you to a range of product and service providers in order to meet your requirements;</li>
                                <li>Administer cases and assist processing of applications;</li>
                                <li>Tell you about any changes to products and service we have arranged for you;</li>
                                <li>Inform you about products and services we think may be suitable for you (unless you ask us not to send you this information).</li>
                            </ul>
                            <p>
                                <strong className="text-brand-primary">2.</strong> We
                                may also process your information in order to meet our
                                legal and regulatory requirements. These may include:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Complying with the regulations set out by the Financial Conduct Authority and other regulators as required;</li>
                                <li>Preventing financial crimes such as money laundering and fraud by passing your details to other relevant bodies such as the police and National Crime Agency where required.</li>
                            </ul>
                            <p>
                                <strong className="text-brand-primary">3.</strong> We
                                may also process your personal data for our own
                                legitimate interests. Typically, these would be limited
                                to:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Analysing our business for trends and research how we can improve our services to customers;</li>
                                <li>Reviewing the work we have carried out for you to ensure we have met the regulations and quality standards;</li>
                                <li>Ensuring we are able to exercise or defend legal challenges in the future.</li>
                            </ul>
                            <p>
                                <strong className="text-brand-primary">4.</strong> HL
                                Partnership Limited will use your data to monitor and
                                assess our work to ensure we have provided a high
                                standard of service and met our legal and regulatory
                                commitments.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="How long we will store your data">
                            <p>
                                We will only keep your personal information as long as
                                necessary. How long that is depends on the type of data
                                and the nature of the work we have carried out. In most
                                cases, your personal data will be stored for an
                                indefinite period as we need to keep evidence that we
                                have fulfilled our legal and regulatory requirements.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="Can I see a copy of my personal data?">
                            <p>
                                Yes. Please write to our Data Controller (details below)
                                and we will provide a copy of any personal data we hold
                                about you within one calendar month of receiving your
                                request. Please note that we may need to verify your
                                identity before providing the information.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="What if my data is incorrect or incomplete?">
                            <p>
                                You have the right to have your data corrected or
                                completed by contacting our Data Controller in writing
                                (details below). Please be aware that we need to
                                maintain records of information as it was at the time of
                                the advice or the transaction taking place.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="Can I have my data erased?">
                            <p>
                                If we have provided you with advice or processed a
                                transaction on your behalf, we will not normally be able
                                to erase your personal data. This is because we are
                                obliged to maintain records of our advice and
                                transactions to meet our regulatory requirements and in
                                case we need to exercise or defend legal claims in
                                future.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="Who is the Data Controller?">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                                <ContactCard
                                    name="Acumen Financial Services Ltd"
                                    address="2/3, 48 George Street, Glasgow, G2 1BP"
                                    telephone="07949 541996"
                                    email="sn@acumenfs.co.uk"
                                    regNo="ZB547072"
                                />
                                <ContactCard
                                    name="HL Partnership Limited"
                                    address="6 Merus Court, Meridian Business Park, Leicester, LE19 1RJ"
                                    telephone="03300 552651"
                                    email="compliance@hlpartnership.co.uk"
                                    regNo="ZB588911"
                                />
                            </div>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="How can I get more information?">
                            <p>
                                If you would like further details on how we use your
                                personal information or if you have any data protection
                                queries, please write to:
                            </p>
                            <div className="bg-brand-surface border-l-4 border-brand-accent p-5 rounded">
                                <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold mb-2">
                                    Data Protection Officer
                                </div>
                                <p className="text-sm text-brand-text leading-relaxed">
                                    The Data Protection Officer, HL Partnership Limited,
                                    6 Merus Court, Meridian Business Park, Leicester,
                                    LE19 1RJ
                                </p>
                                <p className="text-sm mt-2">
                                    Email:{" "}
                                    <a
                                        href="mailto:compliance@hlpartnership.co.uk"
                                        className="link-underline text-brand-primary font-semibold"
                                    >
                                        compliance@hlpartnership.co.uk
                                    </a>
                                </p>
                            </div>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="Making a complaint about how we have handled your data">
                            <p>
                                Please write in the first instance to the Data
                                Protection Officer at the address above. If you are not
                                satisfied with the Data Protection Officer's response,
                                you can make a complaint to the Information
                                Commissioner's Office (ICO):
                            </p>
                            <div className="bg-brand-surface border border-brand-border rounded-xl p-5">
                                <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold mb-3">
                                    Information Commissioner's Office
                                </div>
                                <p className="text-sm text-brand-text leading-relaxed">
                                    Wycliffe House, Water Lane, Wilmslow, Cheshire,
                                    SK9 5AF
                                </p>
                                <p className="text-sm mt-2">
                                    Telephone:{" "}
                                    <a href="tel:03031231113" className="link-underline text-brand-primary font-semibold">
                                        0303 123 1113
                                    </a>{" "}
                                    (local rate) or{" "}
                                    <a href="tel:01625545745" className="link-underline text-brand-primary font-semibold">
                                        01625 545 745
                                    </a>{" "}
                                    (national rate)
                                </p>
                                <p className="text-sm mt-2">
                                    Online:{" "}
                                    <a
                                        href="https://ico.org.uk/concerns/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="link-underline text-brand-primary font-semibold"
                                    >
                                        ico.org.uk/concerns
                                    </a>
                                </p>
                            </div>
                            <p className="text-sm">
                                For complaints about our advice or services, please see
                                our{" "}
                                <Link
                                    to="/complaints"
                                    className="link-underline text-brand-primary font-semibold"
                                >
                                    Complaints Policy
                                </Link>
                                .
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <Section heading="Important notes on protecting your own data">
                            <p>
                                Please be aware of cybercrime and protecting your own
                                information online, particularly financial information.
                                Do not send unsecure personal information over the
                                internet. We recommend you use encryption software or
                                provide any information to us using the online tools we
                                provide.
                            </p>
                            <p>
                                Do not respond to emails asking you to pay money. Never
                                make a payment purely in response to an email. Always
                                call the company to validate any payment requests and
                                check the phone number independently from the email
                                (i.e. via a secure web site). If you receive a phone
                                call asking for payment ensure you are satisfied the
                                call is genuine, or else call the company back to check.
                                Neither Acumen Financial Services Ltd nor HL Partnership
                                will take responsibility if you transfer money to a
                                fraudster in error.
                            </p>
                        </Section>
                    </Reveal>

                    <Reveal>
                        <div className="mt-16 pt-8 border-t border-brand-border text-xs text-brand-muted leading-relaxed">
                            <p>Data Privacy Statement v3 · 11/22rjh</p>
                            <p className="mt-3">
                                Acumen Financial Services Ltd is an appointed
                                representative of HL Partnership Limited, which is
                                authorised and regulated by the Financial Conduct
                                Authority. The guidance and/or information contained
                                within this website is subject to the UK regulatory
                                regime and is therefore targeted at consumers based in
                                the United Kingdom.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </>
    );
}
